import axiosClient from "./request";
import { convertToForm } from "../utils";
import { getJWTFromLocalStorage } from "../localStorage";

async function patchAiAgentChatFeedbackFetch(payload) {
  const base = (process.env.REACT_APP_API_URL || "").replace(/\/?$/, "/");
  const token = getJWTFromLocalStorage();
  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  if (
    typeof process.env.REACT_APP_API_URL === "string" &&
    process.env.REACT_APP_API_URL.toLowerCase().includes("ngrok")
  ) {
    headers["ngrok-skip-browser-warning"] = "true";
  }
  const res = await fetch(
    `${base}ai-agents/${payload.agentId}/chat-history/`,
    {
      method: "PATCH",
      credentials: "include",
      headers,
      body: JSON.stringify({
        message_id: payload.message_id,
        feedback: payload.feedback,
      }),
      keepalive: true,
    }
  );
  if (!res.ok) {
    const err = new Error("Feedback update failed");
    err.response = { status: res.status, data: await res.text() };
    throw err;
  }
  try {
    return await res.json();
  } catch {
    return {};
  }
}

async function streamIdeasFetch(payload, callbacks = {}) {
  const base = (process.env.REACT_APP_API_URL || "").replace(/\/?$/, "/");
  const token = getJWTFromLocalStorage();
  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  if (
    typeof process.env.REACT_APP_API_URL === "string" &&
    process.env.REACT_APP_API_URL.toLowerCase().includes("ngrok")
  ) {
    headers["ngrok-skip-browser-warning"] = "true";
  }

  const response = await fetch(`${base}ai-agents/${payload.companionId}/chat/`, {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify({
      save_data: false,
      stream: true,
      messages: [{ content: payload.prompt, role: "user" }],
    }),
  });

  if (!response.ok || !response.body) {
    const error = new Error("Failed to generate ideas");
    error.response = { status: response.status, data: await response.text() };
    throw error;
  }

  callbacks.onStart?.();

  const reader = response.body.getReader();
  const textDecoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();

    if (done) {
      break;
    }

    buffer += textDecoder.decode(value, { stream: true });
    const lines = buffer.split(/\r?\n/);
    buffer = lines.pop() || "";

    for (const line of lines) {
      const trimmedLine = line.trim();

      if (!trimmedLine) {
        continue;
      }

      if (trimmedLine === "data: [DONE]") {
        callbacks.onDone?.();
        return { done: true };
      }

      const payloadLine = trimmedLine.startsWith("data:")
        ? trimmedLine.slice(5).trim()
        : trimmedLine;

      let chunk = null;

      try {
        chunk = JSON.parse(payloadLine);
      } catch {
        chunk = { response: payloadLine };
      }

      if (
        chunk?.done === true ||
        chunk?.event === "done" ||
        chunk?.response === "done"
      ) {
        callbacks.onDone?.();
        return { done: true };
      }

      callbacks.onDelta?.(chunk);
    }
  }

  if (buffer.trim()) {
    const payloadLine = buffer.trim().startsWith("data:")
      ? buffer.trim().slice(5).trim()
      : buffer.trim();
    let chunk = null;

    try {
      chunk = JSON.parse(payloadLine);
    } catch {
      chunk = { response: payloadLine };
    }

    if (
      chunk?.done !== true &&
      chunk?.event !== "done" &&
      chunk?.response !== "done"
    ) {
      callbacks.onDelta?.(chunk);
    }
  }

  callbacks.onDone?.();
  return { done: true };
}

export const routes = Object.freeze({
  auth: async (payload) => axiosClient.post(`auth/`, payload),
  signUp: async (payload) => axiosClient.post(`signup/`, payload),
  getTags: async (payload) => axiosClient.get(`tags/`, payload),
  getMentors: async (payload) => axiosClient.get("mentors/", payload),
  getScenarios: async (payload) => axiosClient.get("scenarios/", payload),
  setScenarios: async (payload) => axiosClient.post("scenarios/", payload),
  getResults: async (payload) => axiosClient.get("result/", payload),
  getRecommendationsQuestion: async (payload) => axiosClient.get(`question/${payload.id}/refresh/answer/`),
  setResults: async (path, payload) =>
    axiosClient.patch(`result/${path}/`, payload),
  setResultsDetail: async (path, payload) =>
    axiosClient.get(`result/${path}/`, payload),
  getPricingPlan: async (payload) => axiosClient.get("pricing_plan/", payload),
  setPricingPlan: async (path) =>
    axiosClient.patch(`profile/`, { pricing_plan: path }),
  getProfile: async (payload) => axiosClient.get("profile/", payload),
  getOnboarding: async (payload) => axiosClient.get("profile/onboarding/", payload),
  setProfile: async (payload) => {
    const params = payload?.profile_image ? convertToForm(payload) : payload
    const headers = payload?.profile_image ? {
      "Content-Type": "multipart/form-data"  // Add any custom headers here
    } : {};
    return axiosClient.patch(`profile/`, params, { headers })
  },
  updateOnboarding: async (payload) =>
    axiosClient.patch("profile/onboarding/", payload),

  getCareers: async (payload) =>
    axiosClient.get(`careers/catalog`, {
      params: payload,
    }),
  setCareers: async (payload) => axiosClient.post(`careers/interest/`, payload),
  unsetCareers: async (payload) => axiosClient.patch(`result/${payload.id}/`, payload),
  getRecommendations: async (payload) => axiosClient.get(`recommendations/`, payload),
  setPassword: async (payload) => axiosClient.post(`set-password/`, payload),
  getFeedBacks: async (payload) => axiosClient.get("mentor_feedbacks/", payload),
  getSessions: async (payload) => axiosClient.get("mentor_sessions/", payload),
  getIndustries: async (payload) => axiosClient.get("industries/", payload),
  getQuotess: async (payload) => axiosClient.get("user-reviews/", { params: payload }),
  getStudentResourcess: async (payload) => axiosClient.get("school-resource/", { params: payload }),
  getAiAgents: async (payload) => axiosClient.get("ai-agents/", { params: { ...payload } }),
  getStrengths: async (payload) => axiosClient.get("my-strength-profile/", { params: { ...payload } }),
  getAiAgentsChat: async (payload) =>
    axiosClient.post(`ai-agents/${payload.id}/chat/`, {
      messages: [
        { role: "user", content: payload.first_msg },
        ...payload?.history,
        { content: payload.text, role: payload.role },
      ],
    }),
  getIdeasChat: async (payload, callbacks) =>
    streamIdeasFetch(payload, callbacks),
  getAiAgentsChatHistory: async (payload) => axiosClient.get(`ai-agents/${payload.id}/chat-history/`),
  /** Update feedback for one AI message (PATCH). Uses fetch+keepalive so the request can finish if the user navigates away. */
  updateAiAgentsChatFeedback: async (payload) =>
    patchAiAgentChatFeedbackFetch(payload),
  setRecommendationCareers: async (payload) =>
    axiosClient.patch(`result/${payload.id}/`, payload),
  checkConfirmAffiliate: async (payload) => axiosClient.post("affiliate-code/", payload),
  getTerms: async (payload) => axiosClient.get("action-plans/", { params: { ...payload } }),
  deleteTerm: async (id) => axiosClient.delete(`action-plans/${id}/`),
  addUpdateTerm: async (payload, id) => axiosClient[id ? 'patch' : 'post'](`action-plans/${id ?id+'/':''}`, payload),
  downloadAction: async () => axiosClient.get(`/export/career-pdf/`, { responseType: 'blob' }),
  reorderTermAction: async (payload) => axiosClient.patch(`/action-plans/reorder/`, payload),
  promptStatus: async () => axiosClient.get("prompts/status/"),
  promptLog: async (payload) => axiosClient.post("prompts/log/", payload),
});
